import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useAppSelector } from './store/hooks';
import { getMeThunk, logoutThunk } from './store/middlewares/authThunk';
import { setInitializedReducer } from './store/reducers/authReducer';
import { SESSION_EXP_ERR } from './constants/index';
import { showErrorToast } from './utils/toast';
// routes
import Router from './routes';
// theme
import ThemeProvider from './theme';
// components
import ThemeSettings from './components/settings';
import ChartStyle from './components/chart/ChartStyle';
import ScrollToTop from './components/ScrollToTop';
import { ProgressBarStyle } from './components/ProgressBar';
import NotistackProvider from './components/NotistackProvider';
import MotionLazyContainer from './components/animate/MotionLazyContainer';
import ErrorBoundary from './components/ErrorBoundary';

// ----------------------------------------------------------------------

// setTimeout overflows above ~24.8 days, so longer sessions are not scheduled
const MAX_TIMEOUT_MS = 2147483647;

export default function App() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { accessToken, isInitialized, sessionExpiresAt } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (accessToken) {
      // A stored token: refresh who is logged in and the roles (GET /staff/me)
      dispatch(getMeThunk());
    } else if (!isInitialized) {
      // Otherwise, just mark initialized immediately
      dispatch(setInitializedReducer());
    }
  }, [dispatch, accessToken, isInitialized]);

  // Log the staff user out when the token (8 hours, expires_at) runs out
  useEffect(() => {
    if (!accessToken || !sessionExpiresAt) return undefined;

    const msLeft = new Date(sessionExpiresAt).getTime() - Date.now();
    if (Number.isNaN(msLeft) || msLeft > MAX_TIMEOUT_MS) return undefined;

    const expire = () => {
      showErrorToast(SESSION_EXP_ERR);
      dispatch(logoutThunk());
    };

    if (msLeft <= 0) {
      expire();
      return undefined;
    }

    const timer = setTimeout(expire, msLeft);
    return () => clearTimeout(timer);
  }, [dispatch, accessToken, sessionExpiresAt]);

  return (
    <MotionLazyContainer>
      <ThemeProvider>
        <ThemeSettings>
          <NotistackProvider>
            <ProgressBarStyle />
            <ChartStyle />
            <ScrollToTop />
            <ErrorBoundary resetKey={pathname} fullPage>
              <Router />
            </ErrorBoundary>
          </NotistackProvider>
        </ThemeSettings>
      </ThemeProvider>
    </MotionLazyContainer>
  );
}
